/* global AggregateError */

// ----------------------------------------------------------------------------
//   Imports
// ----------------------------------------------------------------------------

// Custom Scripts: Local
import { sleep, getAPIBaseURL } from "../scripts/utils.js";
import { updateRecording } from "./backupRecordings.js";
import { ErrorState, StateError } from "./errorHandling.js";



// ----------------------------------------------------------------------------
//   Helper Functions
// ----------------------------------------------------------------------------

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

/**
 * Request a WakeLock for the screen, returning `null` if the request fails.
 */
async function getOptionalWakeLock() {

  // Request a WakeLock for the screen and return it
  try {
    return await navigator.wakeLock.request("screen");
  }

  // If the WakeLock request fails, skip it
  catch (err) {
    console.log(`Failed to obtain WakeLock: ${err.name}, ${err.message}`);
    return null;
  }
}


/**
 * Request an AWS signed URL from the API.
 * Returns an object containing:
 *   - `url`: The signed AWS URL to upload to
 *   - `filename`: The name of the file in S3.
 */
async function getUploadURL( recording ) {

  // Construct the route in the API
  const apiRoute = getAPIBaseURL("upload-url");

  // Add local file title, if specified
  if ( recording.title ) {
    apiRoute.searchParams.append("title", recording.title);
  }

  // Add content type, if specified
  if ( recording.blob?.type ) {
    apiRoute.searchParams.append("contentType", recording.blob.type);
  }
  else {
    apiRoute.searchParams.append("contentType", 'video/webm');
  }

  // Fetch signed URL from API
  try {
    const apiResponse = await fetch(
      apiRoute.toString(),
      {
        credentials: "include",
      }
    )

    if (!apiResponse.ok) {
      throw new Error('Unable to construct upload URL');
    }

    return await apiResponse.json();
  }

  // Intercept error, print where it came from, then propagate
  catch (e) {
    console.error('Unable to construct upload URL');
    throw e;
  }
}



// ----------------------------------------------------------------------------
//   Main Logic
// ----------------------------------------------------------------------------


/**
 * Request an AWS signed URL and upload a video blob to that URL.
 *
 * Args:
 *   - recording: The local database entry with the blob to upload
 *   - onProgress: An optional callback to run with progress updates.
 */
export async function uploadRecording(recording, onProgress, verbose=false) {
  if (verbose) console.log("Uploading...")

  // Promise that resolves when upload is complete, or rejects if upload is interrupted
  return new Promise( async (resolve, reject) => {

    // Request a WakeLock for the screen
    const wakeLock = await getOptionalWakeLock();
    if (verbose) console.log(`Got wakelock: ${wakeLock}`)

    // Use .webm as the filetype
    const filetype = recording.blob?.type ?? 'video/webm';

    // Fetch signed URL from API
    let url;
    let filename;
    try {
      let response = await getUploadURL( recording );
      url = response.url;
      filename = response.filename;

      // Update the local IndexedDB entry with the returned filename
      updateRecording( recording.recordingID, { filename } );
    }
    catch (e) {
      reject(e)
    }

    // Initialize new XHR Request to stream recording blob to AWS
    const xhrRequest = new XMLHttpRequest();
    xhrRequest.open("PUT", url);

    // Set headers
    if (filetype) {
      xhrRequest.setRequestHeader( "Content-Type", filetype );
    }


    // Create error handler
    const handleErr = async (event) => {

      // Release the WakeLock
      if (wakeLock) await wakeLock.release();

      // Raise event to caller
      reject(event);
    }

    // Attach error handler to upload failure conditions
    xhrRequest.addEventListener("abort", handleErr);
    xhrRequest.addEventListener("error", handleErr);


    // Attach progress listener, if provided
    if (onProgress) {
      xhrRequest.upload.addEventListener("progress", ({ loaded, total }) => onProgress(loaded, total));
    }


    // Attach success handler
    xhrRequest.addEventListener("load", async (event) => {
      if (verbose) console.log("Uploaded successfully!", event);

      // Release the WakeLock
      if (wakeLock) await wakeLock.release();

      // Update the local IndexedDB entry with uploaded status and timestamp.
      // If this fails, log the error but do not fail the upload, since the data
      // has already been successfully uploaded to the server.
      try {
        await updateRecording(
          recording.recordingID,
          { uploaded: true, uploadedAt: new Date().toISOString() }
        );
      }
      catch (err) {
        console.error("Failed to mark recording as uploaded in local storage:", err);
      }

      resolve(event);
    });


    // Begin the upload
    xhrRequest.send( recording.blob );
  });
}


/**
 * Upload multiple recordings together.
 *
 * Each element in `recordings` will have the fields:
 *   - recordingID: unique ID in localdb
 *   - title: a local title for the file
 *   - blob: the actual object to upload
 *
 * @param {Array} recordings - Array of recording objects to upload
 * @param {Function} onProgress - Callback for progress updates (loaded, total)
 * @returns {Promise<Array>} - Promise that resolves with an array of results from each upload when all uploads succeed.
 * @throws {StateError} If one or more uploads fail.
 */
export async function uploadRecordingsBatch(recordings, onProgress, verbose = false) {
  if (verbose) console.debug('Uploading recordings: ', recordings )

  // Compute the combined total size of all blobs
  const totalSize = recordings.reduce((sum, recording) => {
    return sum + (recording.blob?.size || 0);
  }, 0);

  // Warn if computed size is 0 (or negative somehow)
  if (totalSize <= 0) {
    console.warn(`Computed total upload size as ${totalSize} bytes.`)
  } else {
    if (verbose) console.log('Total size: ', totalSize)
  }

  // Track progress for each upload
  const progressByRecording = new Map();
  recordings.forEach(recording => {
    progressByRecording.set(recording.recordingID, 0);
  });

  // Helper function to update aggregated progress
  // Note: If totalSize was computed at 0, sends a progress update indicating half the data is loaded.
  // TODO: Smarter totalSize computation based on individual upload calls
  const updateAggregatedProgress = () => {
    const totalLoaded = Array.from(progressByRecording.values()).reduce((sum, loaded) => sum + loaded, 0);
    if (onProgress) {
      onProgress(totalLoaded, totalSize <= 0 ? (totalLoaded * 2) : totalSize);
    }
  };

  // Create upload promises for each recording
  const uploadPromises = recordings.map((recording) => async () => {

    // Create a progress handler for this specific recording
    const recordingProgressHandler = (loaded, total) => {

      // Update this recording's progress
      progressByRecording.set(recording.recordingID, loaded);

      // Update the aggregated progress
      updateAggregatedProgress();
    };

    // Upload the recording
    try {
      const result = await uploadRecording(recording, recordingProgressHandler, verbose);
      if (verbose) console.log(`Successfully uploaded recording ${recording.recordingID}`)

      return {
        success: true,
        recordingId: recording.recordingID,
        filename: result?.filename,
        error: null,
      };
    }
    catch (error) {
      console.error(`Failed to upload recording ${recording.recordingID}:`, error);

      return {
        success: false,
        recordingId: recording.recordingID,
        filename: null,
        error: error,
      };
    }
  });

  // Execute uploads sequentially
  // TODO: Could potentially speed up with parallel execution, but we'd have to ensure the server assigns distinct names to all the files
  const uploadResults = [];
  for (const uploadPromise of uploadPromises) {
    uploadResults.push(await uploadPromise());
  }
  // TODO: Consider clearing successfully uploaded recordings from local storage
  // after successful upload. This would prevent re-uploading the same files.

  // Aggregate errors across all the uploads
  const failedUploads = uploadResults.filter(({success}) => !success);
  if (failedUploads.length) {
    throw new StateError(
      ErrorState.UPLOAD_FAILED,
      new AggregateError(failedUploads.map(({error}) => error))
    );
  }

  // Return the set of results
  return uploadResults;
}



// ----------------------------------------------------------------------------
//   Testing
// ----------------------------------------------------------------------------


/**
 * Drop-in replacement for `uploadRecording` that simply reports
 * progress updates, without performing any actual upload logic.
 *
 * Useful for testing workflow without requiring an actual blob to upload.
 */
export async function spoofUploadRecording( recording, onProgress, throwError = false ) {

  // If an actual blob was passed, log it
  if (recording !== null) {
    console.log("Received recording for upload: ", recording)
  }

  // Parameters
  const numIncrements = 3;
  const delayMS       = 1000;

  // Loop and wait, simulating upload behavior
  for ( let i = 1; i <= numIncrements; i++ ) {
    await sleep( delayMS );
    onProgress( i, numIncrements );

    // Optionally throw an error midway through "uploading"
    if ( throwError && i >= 2 ) throw new Error('Dev error!');
  }

  // Final wait
  await sleep( delayMS );
}
